import { useState, useEffect, useRef, useCallback } from 'react';
  import { api } from '../services/api';
export const useDS5Status = () => {
  const [ds5Status, setDs5Status] = useState({
    connected: false,
    serialNumber: '',
    status: 'unknown',
    date: '',
    message: '',
    isLoading: false
  });

  const lastSerialNumberRef = useRef('');
  const lastStatusResultRef = useRef(null);
  const getCloudStatus = useCallback(async (serialNumber) => {
  if (
    lastSerialNumberRef.current === serialNumber &&
    lastStatusResultRef.current
  ) {
    return lastStatusResultRef.current;
  }

  try {
    console.log(`Requesting status for serial: ${serialNumber}`);

    const data = await api.getSessionStatusBySn(serialNumber);

    if (!data) {
      const result = {
        status: 'unknown',
        date: 'unknown',
        message: ''
      };
      lastStatusResultRef.current = result;
      lastSerialNumberRef.current = serialNumber;
      return result;
    }

    console.log('API response data:', data);

    let result;

    if (data && typeof data === 'object') {
      if (data.globalStatus) {
        result = {
          status: data.globalStatus,
          date: data.startDate || 'unknown',
          message: data.message || ''
        };
      } else {
        result = {
          status: data.globalStatus || data.status || data.result || 'unknown',
          date: data.startDate || data.date || data.testDate || 'unknown',
          message: data.message || data.msg || ''
        };
      }
    } else {
      result = {
        status: 'unknown',
        date: 'unknown',
        message: 'Unexpected response format'
      };
    }

    // Normalize status
    if (result.status) {
      const normalizedStatus = result.status.toLowerCase();
      if (['ok', 'pass', 'success'].includes(normalizedStatus)) {
        result.status = 'ok';
      } else if (['ko', 'fail', 'failed'].includes(normalizedStatus)) {
        result.status = 'ko';
      } else {
        result.status = normalizedStatus;
      }
    }

    console.log('Final normalized result:', result);

    lastStatusResultRef.current = result;
    lastSerialNumberRef.current = serialNumber;
    return result;

  } catch (error) {
    console.error('Error with API:', error);

    const result = {
      status: 'error',
      date: new Date().toLocaleDateString('fr-FR'),
      message: `Connection error: ${error.message}`
    };

    lastStatusResultRef.current = result;
    lastSerialNumberRef.current = serialNumber;
    return result;
  }
}, []);


  const formatDate = useCallback((dateStr) => {
    if (dateStr === 'unknown' || dateStr === 'error' || !dateStr) {
      return dateStr;
    }

    try {
      console.log('Formatting date:', dateStr);

      let cleanDateStr = dateStr.replace('Z', '+00:00').replace(' ', 'T');

      if (!cleanDateStr.includes('+') && !cleanDateStr.includes('Z')) {
        cleanDateStr += 'Z';
      }
      
      const date = new Date(cleanDateStr);
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateStr);
        return dateStr;
      }
      
      const formatted = date.toISOString().split('T')[0]; 
      console.log('Formatted date:', formatted);
      return formatted;
      
    } catch (e) {
      console.error('Error formatting date:', e, 'Original:', dateStr);
      return dateStr;
    }
  }, []);

  const updateDS5Status = useCallback(async (serialNumber) => {
    if (!serialNumber || serialNumber === lastSerialNumberRef.current) {
      return;
    }

    console.log('Starting DS5 status update for:', serialNumber);
    setDs5Status(prev => ({ ...prev, isLoading: true }));

    try {
      const result = await getCloudStatus(serialNumber);
      const formattedDate = formatDate(result.date);

      console.log('DS5 status update completed:', {
        serialNumber,
        status: result.status,
        date: formattedDate,
        message: result.message
      });

      setDs5Status({
        connected: true,
        serialNumber,
        status: result.status,
        date: formattedDate,
        message: result.message,
        isLoading: false
      });
    } catch (error) {
      console.error('Error updating DS5 status:', error);
      setDs5Status(prev => ({
        ...prev,
        isLoading: false,
        status: 'error',
        message: `Error: ${error.message}`
      }));
    }
  }, [getCloudStatus, formatDate]);

  const resetDS5Status = useCallback(() => {
    console.log('Resetting DS5 status');
    setDs5Status({
      connected: false,
      serialNumber: '',
      status: 'unknown',
      date: '',
      message: '',
      isLoading: false
    });
    lastSerialNumberRef.current = '';
    lastStatusResultRef.current = null;
  }, []);

  return {
    ds5Status,
    updateDS5Status,
    resetDS5Status
  };
};